import { Bug, Download } from "lucide-react";
import { LogoEqualizer } from "@/components/LogoVariants";
import { Link } from "react-router-dom";
import { trackNavigation, trackCTA } from "@/lib/analytics";
import { lazy, Suspense, useState } from "react";
import { BrandDisambiguation } from "@/components/BrandDisambiguation";
import { usePWAInstall } from "@/hooks/usePWAInstall";

// Lazy-load the bug report dialog. The Footer renders on every page; the
// dialog only opens when a user clicks "Report a Bug" — rare on most sessions.
// Before this change the BugReportDialog component (~6KB after icon/primitive
// dedup) shipped as part of the main bundle on first paint of every route.
// (Andy 2026-05-08 perf pass — third in the sequence after #251/#252.)
const BugReportDialog = lazy(() =>
  import("@/components/BugReportDialog").then((m) => ({ default: m.BugReportDialog })),
);

export const Footer = () => {
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const { canInstall, install } = usePWAInstall();

  return (
    <>
      <footer className="border-t border-border/40 mt-20">
        <div className="container py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-4">
              <LogoEqualizer size="sm" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                AI-powered playlist generation
                <br />
                for Spotify and YouTube
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                More from us:{" "}
                {/* utm_source is what FitMito's first-touch attribution reads FIRST; without it
                    the visit falls back to the referrer host, which works but cannot tell this
                    footer apart from any other spotivibly.com link we might add later. Naming the
                    medium is the whole point — it is how we find out whether a footer is a real
                    surface or a place links go to be ignored. */}
                <a
                  href="https://fitmito.com/?utm_source=spotivibly&utm_medium=footer"
                  target="_blank"
                  rel="noopener"
                  onClick={() => trackNavigation('fitmito', 'footer')}
                  className="inline-flex min-h-11 items-center text-foreground/80 hover:text-foreground underline underline-offset-4 transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                >
                  FitMito
                </a>
                , a gamified health coach.
              </p>
            </div>

            <nav className="space-y-4" aria-label="Product links">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-foreground/80">Product</p>
              <ul>
                <li>
                  <Link
                    to="/how-it-works"
                    onClick={() => trackNavigation('how_it_works', 'footer')}
                    className="inline-flex min-h-11 items-center text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                  >
                    How It Works
                  </Link>
                </li>
                <li>
                  <Link
                    to="/generate"
                    onClick={() => trackNavigation('generate', 'footer')}
                    className="inline-flex min-h-11 items-center text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                  >
                    Generate Playlist
                  </Link>
                </li>
                <li>
                  <Link
                    to="/playlists"
                    onClick={() => trackNavigation('playlists', 'footer')}
                    className="inline-flex min-h-11 items-center text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                  >
                    Browse Playlists
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog"
                    onClick={() => trackNavigation('blog', 'footer')}
                    className="inline-flex min-h-11 items-center text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                  >
                    Blog
                  </Link>
                </li>
                <li>
                  <Link
                    to="/convert"
                    onClick={() => trackNavigation('converter', 'footer')}
                    className="inline-flex min-h-11 items-center text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                  >
                    Playlist Converter
                  </Link>
                </li>
                <li>
                  {/* Demand validation for a venue tier (2026-08-25). Placed here
                      because the page cannot measure anything with no traffic, and
                      organic ranking takes months. Remove this link if the tier is
                      not built, along with the page and venue_waitlist. */}
                  <Link
                    to="/for-venues"
                    onClick={() => trackNavigation('for_venues', 'footer')}
                    className="inline-flex min-h-11 items-center text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                  >
                    For Venues
                  </Link>
                </li>
                <li>
                  <Link
                    to="/pricing"
                    onClick={() => trackNavigation('pricing', 'footer')}
                    className="inline-flex min-h-11 items-center text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                  >
                    Pricing
                  </Link>
                </li>
                <li>
                  <Link
                    to="/playlist-generator"
                    onClick={() => trackNavigation('playlist_generator', 'footer')}
                    className="inline-flex min-h-11 items-center text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                  >
                    Playlist Generator
                  </Link>
                </li>
                <li>
                  <Link
                    to="/spotify-playlist-creator"
                    onClick={() => trackNavigation('spotify_playlist_creator', 'footer')}
                    className="inline-flex min-h-11 items-center text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                  >
                    Spotify Playlist Creator
                  </Link>
                </li>
                {/* 4th biggest organic page (211 clicks, 5,210 impressions in
                    the 3 months to 2026-08-18) and it was in NEITHER the header
                    nor the footer, which is why GSC counted 7 internal links to
                    it against 217 for /spotify-playlist-creator and 175 for
                    /playlist-generator — its two siblings, both already here.
                    An omission rather than a decision. */}
                <li>
                  <Link
                    to="/youtube-music-playlist-generator"
                    onClick={() => trackNavigation('youtube_music_playlist_generator', 'footer')}
                    className="inline-flex min-h-11 items-center text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                  >
                    YouTube Music Playlist Generator
                  </Link>
                </li>
              </ul>
            </nav>

            <nav className="space-y-4" aria-label="Company links">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-foreground/80">Company</p>
              <ul>
                <li>
                  <Link
                    to="/privacy"
                    onClick={() => trackNavigation('privacy', 'footer')}
                    className="inline-flex min-h-11 items-center text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                  >
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    onClick={() => trackNavigation('terms', 'footer')}
                    className="inline-flex min-h-11 items-center text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                  >
                    Terms
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    onClick={() => trackNavigation('contact', 'footer')}
                    className="inline-flex min-h-11 items-center text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <button
                    onClick={() => setBugReportOpen(true)}
                    // relative tap-target: this is a standalone control in a
                    // footer nav list, not a link inside a sentence, so it needs
                    // the full 44px touch region. py-1.5 gives it real height
                    // too, which keeps the expanded regions of adjacent list
                    // items from overlapping enough to steal each other's taps.
                    className="relative tap-target py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                    aria-label="Report a bug or issue"
                  >
                    <Bug className="h-3 w-3" aria-hidden="true" />
                    Report a Bug
                  </button>
                </li>
                {canInstall && (
                  <li>
                    <button
                      onClick={async () => {
                        trackCTA('pwa_install', 'footer');
                        await install();
                      }}
                      // Same control type, same list, same treatment as
                      // "Report a Bug" above — a standalone footer control
                      // needs the full 44px touch region (design-rules §10).
                      className="relative tap-target py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                      aria-label="Install Spotivibly app"
                    >
                      <Download className="h-3 w-3" aria-hidden="true" />
                      Install App
                    </button>
                  </li>
                )}
              </ul>
            </nav>
          </div>

          {/* Brand Disambiguation for SEO */}
          <div className="mt-12">
            <BrandDisambiguation />
          </div>

          <div className="mt-8 pt-8 border-t border-border/40">
            <p className="text-center text-sm text-muted-foreground">
              © {new Date().getFullYear()} Spotivibly. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {bugReportOpen && (
        <Suspense fallback={null}>
          <BugReportDialog open={bugReportOpen} onOpenChange={setBugReportOpen} />
        </Suspense>
      )}
    </>
  );
};
